import { useState, useEffect, useRef, useCallback } from 'react'
import { useApp } from '../components/AppContext'
import { useNavigate } from 'react-router-dom'
import { CATEGORIES, AI_SUGGESTIONS, CATEGORY_RULES } from '../data/mockData'
import { fmt } from '../lib/tax'
import {
  CheckCircle, X, Check, Sparkles, RotateCcw, ChevronLeft,
  Info, History, Eye, Lock, HelpCircle, Zap
} from 'lucide-react'
import { getClaudeCategory } from '../lib/claudeAI'

/* ─────────────────────────────────────────
   Constants
───────────────────────────────────────── */
const SWIPE_THRESHOLD   = 80   // px — commit swipe
const SWIPE_VELOCITY    = 0.4  // px/ms — velocity commit
const ROTATION_FACTOR   = 0.07 // card tilt per px dragged
const FLY_DISTANCE      = 600  // px — card flies off screen

/* ─────────────────────────────────────────
   Main Component
───────────────────────────────────────── */
export default function Categorise() {
  const { transactions, categoriseTx, undoCategorise, categoryHistory, coaNudgeDone, setCoaNudgeDone, isPeriodLocked, merchantRules } = useApp()
  const navigate = useNavigate()

  const uncategorised = transactions.filter(t => !t.category)
  const categorised   = transactions.filter(t =>  t.category)
  const progress      = transactions.length > 0 ? Math.round((categorised.length / transactions.length) * 100) : 0

  // ── Swipe state ──
  const [dragX, setDragX]         = useState(0)
  const [dragY, setDragY]         = useState(0)
  const [dragging, setDragging]   = useState(false)
  const [flyDir, setFlyDir]       = useState(null)   // 'left' | 'right' | null
  const [justSwiped, setJustSwiped] = useState(null) // 'left' | 'right' for haptic label

  // ── UI state ──
  const [showOther, setShowOther]   = useState(false)
  const [showReason, setShowReason] = useState(false)
  const [activeView, setActiveView] = useState('categorise')
  const [editTx, setEditTx]         = useState(null)
  const [activeFilter, setActiveFilter] = useState('All')
  const [showCOANudge, setShowCOANudge] = useState(false)

  // ── AI state ──
  const [aiSuggestion, setAiSuggestion] = useState(null)
  const [aiLoading, setAiLoading]       = useState(false)

  // ── Refs ──
  const startX    = useRef(0)
  const startY    = useRef(0)
  const startTime = useRef(0)
  const cardRef   = useRef(null)

  const queue   = uncategorised
  const current = queue[0] || null
  const currentId = current?.id
  const allCategories = CATEGORIES

  // Merchant memory
  const prevSameMerchant = categorised.filter(t => t.merchant === current?.merchant)
  const prevCategory  = prevSameMerchant.length > 0 ? prevSameMerchant[0].category : null
  const prevCatLabel  = prevCategory ? allCategories.find(c => c.id === prevCategory)?.label : null

  // Static suggestion fallback
  const suggestion = current ? AI_SUGGESTIONS[current.merchant] : null

  // Best category to use for right-swipe
  const bestCategory = aiSuggestion?.category || suggestion?.category || (current?.type === 'income' ? 'income_freelance' : 'exp_general')

  // ── AI fetch ──
  useEffect(() => {
    if (!current || current.category) return
    setAiSuggestion(null)
    setAiLoading(true)
    getClaudeCategory(current, merchantRules || {}).then(result => {
      setAiSuggestion(result)
      setAiLoading(false)
    })
    setShowOther(false)
    setShowReason(false)
  }, [currentId])

  // ── Apply category (with fly animation) ──
  const applyCategory = useCallback((catId, source = 'manual', direction = null) => {
    if (!current) return
    const dir = direction || (catId === 'personal' ? 'left' : 'right')
    setFlyDir(dir)
    setJustSwiped(dir)

    // Haptic on mobile
    if (navigator.vibrate) navigator.vibrate(30)

    setTimeout(() => {
      categoriseTx(current.id, catId, source)
      setDragX(0); setDragY(0)
      setFlyDir(null); setJustSwiped(null)
      setAiSuggestion(null); setAiLoading(false)
      if (queue.length === 1 && !coaNudgeDone) {
        setShowCOANudge(true); setCoaNudgeDone(true)
      }
    }, 280)
  }, [current, queue.length, categoriseTx, coaNudgeDone, setCoaNudgeDone])

  function undo() {
    if (!categoryHistory.length) return
    undoCategorise(categoryHistory[categoryHistory.length - 1].txId)
  }

  const lastAction = categoryHistory.length > 0 ? categoryHistory[categoryHistory.length - 1] : null

  // ── Touch / Mouse drag handlers ──
  function onDragStart(e) {
    if (flyDir) return
    setDragging(true)
    const point = e.type === 'touchstart' ? e.touches[0] : e
    startX.current = point.clientX
    startY.current = point.clientY
    startTime.current = Date.now()
  }

  function onDragMove(e) {
    if (!dragging || flyDir) return
    const point = e.type === 'touchmove' ? e.touches[0] : e
    setDragX(point.clientX - startX.current)
    setDragY(point.clientY - startY.current)
  }

  function onDragEnd(e) {
    if (!dragging) return
    setDragging(false)

    const elapsed = Date.now() - startTime.current
    const velocity = Math.abs(dragX) / elapsed

    const shouldCommit = Math.abs(dragX) > SWIPE_THRESHOLD || velocity > SWIPE_VELOCITY

    if (shouldCommit) {
      if (dragX > 0) applyCategory(bestCategory, 'swipe', 'right')
      else           applyCategory('personal', 'swipe', 'left')
    } else {
      // Spring back
      setDragX(0); setDragY(0)
    }
  }

  // ── Computed card transform ──
  const rotation = dragX * ROTATION_FACTOR
  const flyX     = flyDir === 'right' ? FLY_DISTANCE : flyDir === 'left' ? -FLY_DISTANCE : dragX
  const flyY     = flyDir ? dragY * 1.5 : dragY * 0.1
  const cardOpacity = flyDir ? 0 : 1

  const cardStyle = {
    position: 'relative', zIndex: 10,
    transform: `translateX(${flyX}px) translateY(${flyY}px) rotate(${flyDir ? rotation * 2 : rotation}deg)`,
    transition: dragging ? 'none' : flyDir ? 'transform .28s cubic-bezier(.25,.46,.45,.94), opacity .28s' : 'transform .22s cubic-bezier(.34,1.56,.64,1)',
    opacity: cardOpacity,
    cursor: dragging ? 'grabbing' : 'grab',
    userSelect: 'none',
    touchAction: 'pan-y',
    willChange: 'transform',
  }

  // ── Overlay opacity ──
  const rightOpacity = Math.min(1, Math.max(0, (dragX - 30) / 60))
  const leftOpacity  = Math.min(1, Math.max(0, (-dragX - 30) / 60))

  // ── Review mode ──
  if (activeView === 'review') {
    return (
      <div className="fade-up">
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setActiveView('categorise')}>
            <ChevronLeft size={14} /> Back
          </button>
          <div>
            <h1 className="page-title">Review</h1>
            <p className="page-sub">{categorised.length} sorted · {uncategorised.length} remaining</p>
          </div>
        </div>

        {/* Filter strip */}
        <div style={{ display:'flex', gap:8, marginBottom:16, overflowX:'auto', paddingBottom:4 }}>
          {['All','Income','Expenses','Personal','Uncategorised'].map(f => (
            <button key={f} className="btn btn-ghost btn-sm"
              onClick={() => setActiveFilter(f)}
              style={{ fontSize:12, padding:'5px 12px', flexShrink:0,
                background: activeFilter===f ? 'var(--teal-glow)' : undefined,
                borderColor: activeFilter===f ? 'var(--teal)' : undefined,
                color: activeFilter===f ? 'var(--teal)' : undefined }}>
              {f}
            </button>
          ))}
        </div>

        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr auto auto auto', gap:12, padding:'10px 16px', background:'rgba(255,255,255,.03)', borderBottom:'1px solid var(--navy-border)', fontSize:11, color:'var(--slate)', textTransform:'uppercase', letterSpacing:'.06em' }}>
            <span>Transaction</span>
            <span style={{ textAlign:'right' }}>Amount</span>
            <span>Category</span>
            <span>Action</span>
          </div>
          {transactions.filter(tx => {
            if (activeFilter === 'All')           return true
            if (activeFilter === 'Income')        return tx.type === 'income'
            if (activeFilter === 'Expenses')      return tx.type === 'expense'
            if (activeFilter === 'Personal')      return tx.category === 'personal'
            if (activeFilter === 'Uncategorised') return !tx.category
            return true
          }).map(tx => {
            const cat = allCategories.find(c => c.id === tx.category)
            const log = categoryHistory.filter(h => h.txId === tx.id)
            return (
              <div key={tx.id} style={{ display:'grid', gridTemplateColumns:'1fr auto auto auto', gap:12, padding:'12px 16px', borderBottom:'1px solid rgba(255,255,255,.04)', alignItems:'center' }}>
                <div style={{ minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{tx.description}</div>
                  <div style={{ fontSize:11, color:'var(--slate-dim)' }}>{tx.date}
                    {log.length > 1 && <span style={{ color:'var(--amber)', marginLeft:6 }}>· edited {log.length-1}×</span>}
                  </div>
                </div>
                <div style={{ fontSize:13, fontWeight:600, color: tx.type==='income' ? 'var(--teal)' : 'var(--white)', whiteSpace:'nowrap' }}>
                  {tx.type==='income' ? '+' : '-'}{fmt(tx.amount)}
                </div>
                <div>
                  {cat ? (
                    <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                      <span>{cat.emoji}</span>
                      <span style={{ fontSize:12, color:'var(--white)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:100 }}>{cat.label}</span>
                    </div>
                  ) : (
                    <span className="badge badge-amber" style={{ fontSize:10 }}>Needs review</span>
                  )}
                </div>
                <div>
                  {editTx === tx.id ? (
                    <div style={{ display:'flex', gap:4, flexWrap:'wrap', position:'relative', zIndex:50 }}>
                      {allCategories.slice(0,8).map(c => (
                        <button key={c.id} onClick={() => { categoriseTx(tx.id, c.id, 'review_edit'); setEditTx(null) }}
                          className="btn btn-ghost btn-sm" style={{ fontSize:11, padding:'3px 7px' }}>
                          {c.emoji} {c.label.split(' ')[0]}
                        </button>
                      ))}
                      <button onClick={() => setEditTx(null)} className="btn btn-ghost btn-sm" style={{ fontSize:11, padding:'3px 7px' }}>✕</button>
                    </div>
                  ) : (
                    <button onClick={() => setEditTx(tx.id)} className="btn btn-ghost btn-sm" style={{ fontSize:11, padding:'4px 8px' }}>Edit</button>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        <div style={{ marginTop:16, padding:'12px 16px', background:'var(--navy-card)', border:'1px solid var(--navy-border)', borderRadius:'var(--r)', display:'flex', alignItems:'center', gap:10 }}>
          <History size={14} color="var(--slate)" />
          <span style={{ fontSize:12, color:'var(--slate)' }}>
            {categoryHistory.length} actions logged · {categorised.length} of {transactions.length} done
          </span>
        </div>
      </div>
    )
  }

  // ── All done ──
  if (showCOANudge) return (
    <div className="fade-up" style={{ maxWidth:480, margin:'60px auto', textAlign:'center', padding:'0 16px' }}>
      <div style={{ fontSize:64, marginBottom:16 }}>✅</div>
      <h2 style={{ fontFamily:'var(--font-display)', fontSize:28, marginBottom:10 }}>All sorted!</h2>
      <p style={{ color:'var(--slate)', marginBottom:28, fontSize:15, lineHeight:1.6 }}>
        Your tax estimate is now accurate. Ready to submit or want to review your choices?
      </p>
      <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
        <button className="btn btn-ghost" onClick={() => { setShowCOANudge(false); navigate('/dashboard') }}>Go to dashboard</button>
        <button className="btn btn-primary" onClick={() => { setShowCOANudge(false); setActiveView('review') }}>Review categories</button>
      </div>
    </div>
  )

  if (uncategorised.length === 0) return (
    <div className="fade-up">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 className="page-title">Sort</h1>
          <p className="page-sub">All {transactions.length} transactions sorted</p>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => setActiveView('review')}>
          <Eye size={14} /> Review all
        </button>
      </div>
      <div style={{ textAlign:'center', padding:'48px 0 24px' }}>
        <CheckCircle size={52} color="var(--teal)" style={{ marginBottom:16 }} />
        <h2 style={{ fontFamily:'var(--font-display)', fontSize:24, marginBottom:8 }}>All caught up</h2>
        <p style={{ color:'var(--slate)', fontSize:14, marginBottom:24, lineHeight:1.6 }}>Every transaction is categorised. Your tax estimate is live.</p>
        <button className="btn btn-ghost" onClick={() => setActiveView('review')}>
          <Eye size={14} /> Review & edit
        </button>
      </div>
    </div>
  )

  /* ══════════════════════════════════════
     MAIN SWIPE VIEW
  ══════════════════════════════════════ */
  return (
    <div className="fade-up" style={{ minHeight:'80vh', display:'flex', flexDirection:'column' }}>

      {/* ── Top bar ── */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14, flexWrap:'wrap', gap:8 }}>
        <div>
          <p style={{ fontSize:13, color:'var(--slate)' }}>
            {queue.length} to review · {transactions.length} total
          </p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {lastAction && (
            <button className="btn btn-ghost btn-sm" onClick={undo} style={{ fontSize:12 }}>
              <RotateCcw size={13} /> Undo
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => setActiveView('review')} style={{ fontSize:12 }}>
            <Eye size={13} /> All transactions
          </button>
        </div>
      </div>

      {/* ── Tab switcher ── */}
      <div style={{ display:'flex', background:'var(--navy-card)', border:'1px solid var(--navy-border)', borderRadius:10, padding:3, marginBottom:16, gap:3 }}>
        {['Review queue', 'All transactions'].map((tab, i) => {
          const isActive = (i === 0 && activeView === 'categorise') || (i === 1 && activeView === 'review')
          return (
            <button key={tab} onClick={() => setActiveView(i === 0 ? 'categorise' : 'review')}
              style={{ flex:1, padding:'7px 12px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight:500, fontFamily:'var(--font-body)', transition:'all .18s',
                background: isActive ? 'var(--teal)' : 'transparent',
                color: isActive ? 'var(--navy)' : 'var(--slate)' }}>
              {i === 0 ? `Review queue (${queue.length})` : 'All transactions'}
            </button>
          )
        })}
      </div>

      {/* ── Progress ── */}
      <div style={{ marginBottom:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
          <span style={{ fontSize:12, color:'var(--slate)' }}>{categorised.length} of {transactions.length} done</span>
          <span style={{ fontSize:12, color:'var(--teal)', fontWeight:700 }}>{progress}%</span>
        </div>
        <div className="prog-track" style={{ height:5 }}>
          <div className="prog-fill" style={{ width:`${progress}%`, background:'var(--teal)', transition:'width .5s ease' }} />
        </div>
      </div>

      {/* ── Swipe direction hints ── */}
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10, fontSize:12, padding:'0 4px' }}>
        <span style={{ color: leftOpacity > 0 ? 'var(--red)' : 'var(--slate-dim)', transition:'color .1s', display:'flex', alignItems:'center', gap:4, fontWeight: leftOpacity > 0 ? 700 : 400 }}>
          ← Personal
        </span>
        <span style={{ color: rightOpacity > 0 ? 'var(--teal)' : 'var(--slate-dim)', transition:'color .1s', display:'flex', alignItems:'center', gap:4, fontWeight: rightOpacity > 0 ? 700 : 400 }}>
          Business →
        </span>
      </div>

      {/* ── Card stack ── */}
      {current && (
        <div style={{ position:'relative', flex:1 }}>

          {/* Card 3 (furthest back) */}
          {queue.length > 2 && (
            <div style={{
              position:'absolute', top:14, left:'7%', right:'7%', bottom:0,
              background:'var(--navy-mid)', border:'1px solid var(--navy-border)',
              borderRadius:'var(--r-lg)', zIndex:1,
              transform: `scale(${0.88 + rightOpacity * 0.04 + leftOpacity * 0.04})`,
              transition:'transform .22s ease',
            }} />
          )}

          {/* Card 2 (behind) */}
          {queue.length > 1 && (
            <div style={{
              position:'absolute', top:7, left:'3.5%', right:'3.5%', bottom:0,
              background:'var(--navy-card)', border:'1px solid var(--navy-border)',
              borderRadius:'var(--r-lg)', zIndex:2,
              transform: `scale(${0.94 + rightOpacity * 0.04 + leftOpacity * 0.04})`,
              transition:'transform .22s ease',
            }} />
          )}

          {/* ── MAIN CARD ── */}
          <div
            ref={cardRef}
            className={`swipe-card ${dragX > 40 ? 'right-drag' : dragX < -40 ? 'left-drag' : ''}`}
            style={{ ...cardStyle, padding:0, overflow:'hidden' }}
            onMouseDown={onDragStart} onMouseMove={onDragMove} onMouseUp={onDragEnd}
            onMouseLeave={() => { if (dragging) onDragEnd() }}
            onTouchStart={onDragStart} onTouchMove={onDragMove} onTouchEnd={onDragEnd}
          >
            {/* ── BUSINESS overlay (right swipe) ── */}
            <div style={{
              position:'absolute', inset:0, zIndex:20, pointerEvents:'none',
              background:`rgba(0,212,170,${rightOpacity * 0.15})`,
              borderRadius:'var(--r-lg)',
              border: rightOpacity > 0 ? `2px solid rgba(0,212,170,${rightOpacity * 0.8})` : 'none',
              display:'flex', alignItems:'center', justifyContent:'flex-start', padding:'0 24px',
              opacity: rightOpacity,
              transition: dragging ? 'none' : 'opacity .15s',
            }}>
              <div style={{
                background:'var(--teal)', color:'var(--navy)', borderRadius:12,
                padding:'8px 18px', fontSize:15, fontWeight:800, letterSpacing:'.06em',
                boxShadow:'0 4px 20px rgba(0,212,170,.4)',
                display:'flex', alignItems:'center', gap:8,
                transform:`scale(${0.8 + rightOpacity * 0.2})`,
              }}>
                <Check size={16} strokeWidth={3} /> BUSINESS
              </div>
            </div>

            {/* ── PERSONAL overlay (left swipe) ── */}
            <div style={{
              position:'absolute', inset:0, zIndex:20, pointerEvents:'none',
              background:`rgba(255,92,92,${leftOpacity * 0.15})`,
              borderRadius:'var(--r-lg)',
              border: leftOpacity > 0 ? `2px solid rgba(255,92,92,${leftOpacity * 0.8})` : 'none',
              display:'flex', alignItems:'center', justifyContent:'flex-end', padding:'0 24px',
              opacity: leftOpacity,
              transition: dragging ? 'none' : 'opacity .15s',
            }}>
              <div style={{
                background:'var(--red)', color:'#fff', borderRadius:12,
                padding:'8px 18px', fontSize:15, fontWeight:800, letterSpacing:'.06em',
                boxShadow:'0 4px 20px rgba(255,92,92,.4)',
                display:'flex', alignItems:'center', gap:8,
                transform:`scale(${0.8 + leftOpacity * 0.2})`,
              }}>
                <X size={16} strokeWidth={3} /> PERSONAL
              </div>
            </div>

            {/* ── Card inner content ── */}
            <div style={{ padding:'20px 20px 0', position:'relative', zIndex:5 }}>

              {/* Transaction type + date */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                <span className={`badge ${current.type==='income' ? 'badge-teal' : 'badge-slate'}`} style={{ fontSize:11 }}>
                  {current.type === 'income' ? '↓ MONEY IN' : '↑ MONEY OUT'}
                </span>
                <span style={{ fontSize:11, color:'var(--slate-dim)', letterSpacing:'.02em' }}>{current.date}</span>
              </div>

              {/* Amount */}
              <div style={{ fontFamily:'var(--font-display)', fontSize:42, lineHeight:1, marginBottom:6, letterSpacing:'-.02em' }}>
                {fmt(current.amount)}
              </div>

              {/* Merchant */}
              <div style={{ fontSize:16, color:'var(--off-white)', fontWeight:600, marginBottom:2, letterSpacing:'-.01em' }}>
                {current.description}
              </div>
              <div style={{ fontSize:12, color:'var(--slate-dim)', marginBottom:20 }}>
                via {current.merchant}
              </div>

              {/* ── Memory nudge ── */}
              {prevCatLabel && (
                <div style={{ background:'rgba(0,212,170,.06)', border:'1px solid rgba(0,212,170,.15)', borderRadius:10, padding:'10px 14px', marginBottom:14, display:'flex', alignItems:'center', gap:10 }}>
                  <History size={13} color="var(--teal)" style={{ flexShrink:0 }} />
                  <span style={{ fontSize:12, color:'var(--slate)', flex:1 }}>
                    You marked <strong style={{color:'var(--white)'}}>{current.merchant}</strong> as <strong style={{color:'var(--teal)'}}>{prevCatLabel}</strong> before
                  </span>
                  <button
                    className="btn btn-sm"
                    style={{ fontSize:12, padding:'5px 12px', background:'var(--teal)', color:'var(--navy)', border:'none', fontWeight:700, flexShrink:0 }}
                    onClick={() => applyCategory(prevCategory, 'consistency')}>
                    Yes
                  </button>
                </div>
              )}

              {/* ── Quarterly AI suggestion ── */}
              {!prevCatLabel && (
                <div style={{ marginBottom:16 }}>
                  {aiLoading && (
                    <div style={{ display:'flex', alignItems:'center', gap:10, padding:'11px 14px', background:'rgba(155,127,255,.07)', border:'1px solid rgba(155,127,255,.18)', borderRadius:10, marginBottom:0 }}>
                      <div className="spin" style={{ width:14, height:14, borderColor:'rgba(155,127,255,.3)', borderTopColor:'#9B7FFF' }} />
                      <span style={{ fontSize:13, color:'#9B7FFF' }}>Quarterly AI is working on this...</span>
                    </div>
                  )}

                  {aiSuggestion && !aiLoading && (() => {
                    const cat = allCategories.find(c => c.id === aiSuggestion.category)
                    return (
                      <div>
                        <button
                          onClick={() => applyCategory(aiSuggestion.category, 'quarterly_ai')}
                          style={{ width:'100%', background:'linear-gradient(135deg,rgba(155,127,255,.12),rgba(0,212,170,.06))', border:'1px solid rgba(155,127,255,.4)', borderRadius:10, padding:'11px 14px', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer', marginBottom:6, transition:'border-color .15s' }}
                          onMouseEnter={e => e.currentTarget.style.borderColor='rgba(155,127,255,.75)'}
                          onMouseLeave={e => e.currentTarget.style.borderColor='rgba(155,127,255,.4)'}
                        >
                          <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                            <Zap size={14} color="#9B7FFF" />
                            <div style={{ textAlign:'left' }}>
                              <div style={{ fontSize:13, color:'#9B7FFF', fontWeight:700 }}>Quarterly AI: {cat?.label || aiSuggestion.category}</div>
                              <div style={{ fontSize:11, color:'var(--slate-dim)', marginTop:1 }}>{Math.round(aiSuggestion.confidence * 100)}% confidence</div>
                            </div>
                          </div>
                          <span style={{ fontSize:12, color:'#9B7FFF', background:'rgba(155,127,255,.18)', borderRadius:6, padding:'4px 10px', fontWeight:700, flexShrink:0 }}>Apply</span>
                        </button>
                        <button
                          onClick={() => setShowReason(r => !r)}
                          style={{ background:'none', border:'none', cursor:'pointer', fontSize:11, color:'var(--slate-dim)', display:'flex', alignItems:'center', gap:5, padding:'2px 0 10px' }}>
                          <HelpCircle size={11} /> Why did Quarterly AI suggest this?
                        </button>
                        {showReason && (
                          <div className="fade-in" style={{ background:'rgba(155,127,255,.06)', border:'1px solid rgba(155,127,255,.15)', borderRadius:9, padding:'10px 13px', marginBottom:10, fontSize:12, color:'var(--slate)', lineHeight:1.65 }}>
                            {aiSuggestion.reason}
                          </div>
                        )}
                      </div>
                    )
                  })()}

                  {!aiSuggestion && !aiLoading && suggestion && (
                    <button
                      onClick={() => applyCategory(suggestion.category, 'ai')}
                      style={{ width:'100%', background:'var(--teal-glow)', border:'1px solid rgba(0,212,170,.2)', borderRadius:10, padding:'10px 14px', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                        <Sparkles size={13} color="var(--teal)" />
                        <span style={{ fontSize:13, color:'var(--teal)', fontWeight:500 }}>Suggested: {suggestion.label}</span>
                      </div>
                      <span style={{ fontSize:11, color:'var(--teal)', background:'rgba(0,212,170,.12)', borderRadius:6, padding:'3px 9px', fontWeight:700 }}>Apply</span>
                    </button>
                  )}
                </div>
              )}

              {/* ── Category grid ── */}
              <div style={{ marginBottom:0 }}>
                <div style={{ fontSize:10, color:'var(--slate-dim)', marginBottom:8, textTransform:'uppercase', letterSpacing:'.09em' }}>
                  {current.type === 'income' ? 'Choose income type' : 'Choose expense type'}
                </div>

                {!showOther ? (
                  <div style={{ display:'flex', flexWrap:'wrap', gap:7 }}>
                    {allCategories
                      .filter(c => current.type === 'income' ? c.type === 'income' : c.type === 'expense')
                      .slice(0, 6)
                      .map(cat => (
                        <button key={cat.id}
                          className="btn btn-ghost btn-sm"
                          style={{ fontSize:12.5, padding:'7px 12px',
                            borderColor: aiSuggestion?.category === cat.id ? 'rgba(155,127,255,.5)' : suggestion?.category === cat.id ? 'var(--teal)' : undefined,
                            color: aiSuggestion?.category === cat.id ? '#9B7FFF' : suggestion?.category === cat.id ? 'var(--teal)' : undefined,
                          }}
                          onClick={() => applyCategory(cat.id)}>
                          {cat.emoji} {cat.label}
                        </button>
                      ))
                    }
                    <button className="btn btn-ghost btn-sm" style={{ fontSize:12.5, padding:'7px 12px', borderStyle:'dashed' }}
                      onClick={() => setShowOther(true)}>
                      ✨ More...
                    </button>
                    <button className="btn btn-sm" style={{ fontSize:12.5, padding:'7px 12px', background:'rgba(255,92,92,.08)', color:'var(--red)', border:'1px solid rgba(255,92,92,.2)' }}
                      onClick={() => applyCategory('personal')}>
                      🏠 Personal
                    </button>
                  </div>
                ) : (
                  <div className="fade-in">
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                      <button onClick={() => setShowOther(false)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--slate)', display:'flex', alignItems:'center', gap:4, fontSize:12, padding:0 }}>
                        <RotateCcw size={12} /> Back
                      </button>
                      <span style={{ fontSize:11, color:'var(--slate-dim)', textTransform:'uppercase', letterSpacing:'.07em' }}>All categories</span>
                    </div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:7 }}>
                      {allCategories.filter(c => current.type === 'income' ? c.type === 'income' : c.type !== 'income').map(cat => (
                        <button key={cat.id} className="btn btn-ghost btn-sm" style={{ fontSize:12.5, padding:'7px 12px' }}
                          onClick={() => applyCategory(cat.id)}>
                          {cat.emoji} {cat.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {current.type === 'expense' && !showOther && (
                <div style={{ marginTop:10, marginBottom:0, fontSize:11, color:'var(--slate-dim)', display:'flex', alignItems:'flex-start', gap:5, lineHeight:1.5 }}>
                  <Info size={11} style={{ flexShrink:0, marginTop:1 }} />
                  <span>Meals &amp; Entertainment: HMRC allows only 50% — applied automatically.</span>
                </div>
              )}
            </div>

            {/* ── Swipe action buttons (pinned to card bottom) ── */}
            <div style={{ display:'flex', gap:10, padding:'16px 20px 20px', background:'rgba(0,0,0,.08)', borderTop:'1px solid rgba(255,255,255,.04)', marginTop:16 }}>
              {/* Personal button */}
              <button
                className="btn btn-sm"
                style={{ flex:1, justifyContent:'center', background:'rgba(255,92,92,.1)', color:'var(--red)', border:'1px solid rgba(255,92,92,.25)', fontSize:14, padding:'12px', borderRadius:12, transition:'all .15s' }}
                onMouseEnter={e => { e.currentTarget.style.background='rgba(255,92,92,.2)' }}
                onMouseLeave={e => { e.currentTarget.style.background='rgba(255,92,92,.1)' }}
                onClick={() => applyCategory('personal', 'button', 'left')}>
                <X size={16} strokeWidth={2.5} /> Personal
              </button>

              {/* Business button */}
              <button
                className="btn btn-sm"
                style={{ flex:1.4, justifyContent:'center', background:'var(--teal)', color:'var(--navy)', border:'none', fontSize:14, fontWeight:700, padding:'12px', borderRadius:12, transition:'all .15s' }}
                onMouseEnter={e => { e.currentTarget.style.background='var(--teal-dim)' }}
                onMouseLeave={e => { e.currentTarget.style.background='var(--teal)' }}
                onClick={() => applyCategory(bestCategory, 'button', 'right')}>
                <Check size={16} strokeWidth={2.5} /> Business
              </button>
            </div>
          </div>

          {/* ── Next up preview ── */}
          {queue.length > 1 && (
            <div style={{ textAlign:'center', marginTop:12, fontSize:12, color:'var(--slate-dim)' }}>
              Next: <span style={{ color:'var(--slate)' }}>{queue[1]?.description}</span> · {fmt(queue[1]?.amount)}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
